import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { Calendar, Clock, Flame, CheckCircle2, Circle, BookOpen, Layers, CheckSquare, Activity, FileText, Target, TrendingUp, RefreshCw, X } from "lucide-react";

const KIND_ICONS = { lesson: BookOpen, flashcards: Layers, quiz: CheckSquare, scenario: Activity, summary: FileText };
const KIND_LABELS = {
  en: { lesson: "Lesson", flashcards: "Flashcards", quiz: "Quiz", scenario: "Scenario", summary: "Summary" },
  ar: { lesson: "الدرس", flashcards: "بطاقات", quiz: "اختبار", scenario: "سيناريو", summary: "ملخّص" },
};

// Flat lookup for the Create/Update-Plan submit button to avoid nested ternaries.
function saveButtonLabel({ saving, existing, lang }) {
  if (saving) return lang === "ar" ? "جارٍ الحفظ…" : "Saving…";
  if (existing) return lang === "ar" ? "تحديث" : "Update plan";
  return lang === "ar" ? "إنشاء الخطة" : "Create plan";
}

export default function StudyPlan({ chapters }) {
  const { lang, isRTL } = useLanguage();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/plan/me");
      setPlan(r.data.plan);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="bg-white border border-border rounded-2xl p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  if (!plan || editing) {
    return <PlanSetup existing={plan} onSaved={() => { setEditing(false); load(); }} onCancel={() => setEditing(false)} />;
  }

  const chMap = Object.fromEntries(chapters.map((c) => [c.id, c]));
  const doneAll = plan.done_count >= plan.total_tasks;
  const pct = plan.total_tasks ? Math.round((plan.done_count / plan.total_tasks) * 100) : 0;
  const tasksToday = plan.today_tasks || [];
  const overdue = plan.overdue_tasks || [];

  return (
    <div className="space-y-5" data-testid="study-plan">
      {/* Header strip */}
      <div className="grid sm:grid-cols-4 gap-3">
        <PlanStat icon={Calendar} label={lang === "ar" ? "أيام متبقية" : "Days to exam"} value={plan.days_left} accent />
        <PlanStat icon={Flame} label={lang === "ar" ? "سلسلة" : "Streak"} value={`${plan.streak} ${lang === "ar" ? "يوم" : "day" + (plan.streak === 1 ? "" : "s")}`} />
        <PlanStat icon={Target} label={lang === "ar" ? "التقدّم" : "Progress"} value={`${pct}%`} />
        <PlanStat icon={Clock} label={lang === "ar" ? "يوميًا" : "Per day"} value={`${plan.minutes_per_day}m`} />
      </div>

      {/* Progress bar */}
      <div className="bg-white border border-border rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-semibold">
            {lang === "ar" ? "تقدّم خطة الدراسة" : "Study plan progress"}
          </div>
          <button
            onClick={() => setEditing(true)}
            data-testid="edit-plan"
            className="text-xs text-muted-foreground hover:text-sage inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3.5 h-3.5" /> {lang === "ar" ? "تعديل الخطة" : "Edit plan"}
          </button>
        </div>
        <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-sage to-sage-light transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span>{plan.done_count} / {plan.total_tasks} {lang === "ar" ? "مهمة" : "tasks"}</span>
          <span>{lang === "ar" ? "الامتحان" : "Exam"}: {plan.exam_date}</span>
        </div>
      </div>

      {/* Overdue */}
      {overdue.length > 0 && (
        <div className="bg-terracotta/5 border border-terracotta/30 rounded-2xl p-5" data-testid="overdue-block">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-base font-semibold text-terracotta inline-flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> {lang === "ar" ? "مؤجّلة" : "Catch-up"} ({overdue.length})
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {overdue.map((t) => (
              <TaskRow key={`${t.chapter_id}-${t.kind}-${t.date}`} task={t} chapter={chMap[t.chapter_id]} lang={lang} isRTL={isRTL} variant="overdue" />
            ))}
          </div>
        </div>
      )}

      {/* Today */}
      <div className="bg-white border border-border rounded-2xl p-5" data-testid="today-tasks">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold">
            {lang === "ar" ? "مهام اليوم" : "Today's tasks"}
          </div>
          <span className="text-xs text-muted-foreground">
            {tasksToday.filter((t) => t.done).length}/{tasksToday.length}
          </span>
        </div>
        {tasksToday.length === 0 && (
          <div className="text-sm text-muted-foreground py-6 text-center">
            {doneAll
              ? (lang === "ar" ? "أحسنت! لقد أكملت جميع مهام خطتك 🎉" : "You've completed every task on your plan 🎉")
              : (lang === "ar" ? "لا توجد مهام مقرّرة لهذا اليوم." : "No tasks scheduled for today.")}
          </div>
        )}
        <div className="space-y-2">
          {tasksToday.map((t) => (
            <TaskRow key={`${t.chapter_id}-${t.kind}-${t.date}`} task={t} chapter={chMap[t.chapter_id]} lang={lang} isRTL={isRTL} />
          ))}
        </div>
      </div>
    </div>
  );
}

function TaskRow({ task, chapter, lang, isRTL, variant }) {
  const Icon = KIND_ICONS[task.kind] || BookOpen;
  if (!chapter) return null;
  let stateClasses;
  if (task.done) stateClasses = "bg-sage/5 border-sage/30";
  else if (variant === "overdue") stateClasses = "bg-white border-terracotta/30 hover:border-terracotta";
  else stateClasses = "bg-bone/60 border-border hover:border-sage/40 hover:bg-white";
  return (
    <Link
      to={`/chapter/${task.chapter_id}?tab=${task.kind}`}
      data-testid={`task-${task.chapter_id}-${task.kind}`}
      className={`flex items-center gap-3 p-3 rounded-xl border transition-all group ${stateClasses}`}
    >
      {task.done ? (
        <CheckCircle2 className="w-5 h-5 text-sage shrink-0" strokeWidth={1.8} />
      ) : (
        <Circle className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.8} />
      )}
      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${chapter.gradient} flex items-center justify-center text-white shrink-0`}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Ch{chapter.number} · {KIND_LABELS[lang][task.kind]}
        </div>
        <div className={`text-sm font-medium truncate ${task.done ? "line-through text-muted-foreground" : ""}`}>
          {chapter.title}
        </div>
      </div>
    </Link>
  );
}

function PlanStat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 flex items-center gap-3 ${accent ? "bg-gradient-to-br from-sage/8 to-terracotta/5 border-sage/20" : "bg-white border-border"}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent ? "bg-sage text-white" : "bg-sage/10 text-sage"}`}>
        <Icon className="w-5 h-5" strokeWidth={1.7} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-0.5">{label}</div>
        <div className="font-display text-xl font-semibold tracking-tight">{value}</div>
      </div>
    </div>
  );
}

function PlanSetup({ existing, onSaved, onCancel }) {
  const { lang } = useLanguage();
  const today = new Date();
  const defaultDate = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [examDate, setExamDate] = useState(existing?.exam_date || defaultDate);
  const [minutes, setMinutes] = useState(existing?.minutes_per_day || 45);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      await api.post("/plan", { exam_date: examDate, minutes_per_day: minutes });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save plan");
    } finally { setSaving(false); }
  };

  const deletePlan = async () => {
    if (!window.confirm(lang === "ar" ? "حذف خطة الدراسة؟" : "Delete your study plan?")) return;
    await api.delete("/plan");
    onSaved();
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6 sm:p-8 relative" data-testid="plan-setup">
      {existing && (
        <button onClick={onCancel} className="absolute top-4 end-4 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      )}
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-sage flex items-center justify-center text-white">
          <Calendar className="w-6 h-6" strokeWidth={1.7} />
        </div>
        <div>
          <h3 className="font-display text-xl font-semibold">
            {existing
              ? (lang === "ar" ? "تحديث خطة الدراسة" : "Update your study plan")
              : (lang === "ar" ? "ابدأ خطة دراسة يومية" : "Start a daily study plan")}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            {lang === "ar"
              ? "سنوزّع كل دروس المنهج على الأيام حتى الامتحان — بوتيرة تناسبك."
              : "We'll spread the whole course across the days until your exam — at your pace."}
          </p>
        </div>
      </div>

      <form onSubmit={save} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              {lang === "ar" ? "تاريخ الامتحان" : "Exam date"}
            </span>
            <input
              type="date"
              value={examDate}
              min={new Date(Date.now() + 24*60*60*1000).toISOString().slice(0,10)}
              onChange={(e) => setExamDate(e.target.value)}
              data-testid="plan-exam-date"
              className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-border bg-bone focus:outline-none focus:border-sage"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">
              {lang === "ar" ? "دقائق يوميًا" : "Minutes per day"}
            </span>
            <select
              value={minutes}
              onChange={(e) => setMinutes(parseInt(e.target.value, 10))}
              data-testid="plan-minutes"
              className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-border bg-bone focus:outline-none focus:border-sage"
            >
              {[20, 30, 45, 60, 90, 120].map((m) => (
                <option key={m} value={m}>{`${m} min`}</option>
              ))}
            </select>
          </label>
        </div>
        {error && <div className="text-sm text-terracotta" data-testid="plan-error">{error}</div>}
        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="plan-save"
            className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark disabled:opacity-40 text-white font-medium px-5 py-2.5 rounded-xl transition-all"
          >
            {saveButtonLabel({ saving, existing, lang })}
          </button>
          {existing && (
            <button type="button" onClick={deletePlan} className="text-xs text-terracotta hover:underline">
              {lang === "ar" ? "حذف الخطة" : "Delete plan"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
